import { useCallback, useRef, useState } from 'react';
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';
import { FileUploadStore } from '@sib/files';
import { FileUploadMode } from '@sib/types';
import { useDialog } from '@sib/ui/hooks';
import { useOnDropHandler } from '@sib/utils';

interface UseFileDropDialogProps {
  fileUploadStore: FileUploadStore;
  dropzoneConfig: Partial<DropzoneOptions>;
}

export const useFileDropDialog = ({
  fileUploadStore,
  dropzoneConfig,
}: UseFileDropDialogProps) => {
  const dragCountRef = useRef(0);
  const [currentMode, setCurrentMode] = useState<FileUploadMode | null>(null);
  const { addFiles } = fileUploadStore;
  const { onDrop: handleFileDrop } = useOnDropHandler({ addFiles });
  const { isOpen } = useDialog();

  const onDragEnter = (event: React.DragEvent) => {
    event.preventDefault();

    if (currentMode === FileUploadMode.FileManager || !!isOpen) return;

    dragCountRef.current += 1;
    if (dragCountRef.current === 1) {
      setCurrentMode(FileUploadMode.Dropzone);
    }
  };

  const onDragLeave = (event: React.DragEvent) => {
    event.preventDefault();

    dragCountRef.current -= 1;
    if (dragCountRef.current === 0) {
      setCurrentMode(null);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (currentMode === FileUploadMode.FileManager || isOpen) return;
      handleFileDrop(acceptedFiles, fileRejections);
      dragCountRef.current = 0;
      setCurrentMode(FileUploadMode.FileManager);
    },
    [handleFileDrop, currentMode, isOpen]
  );

  const { getRootProps, getInputProps } = useDropzone({
    ...dropzoneConfig,
    onDrop,
    noClick: true,
    noKeyboard: true,
    onDragEnter,
    onDragLeave,
  });

  const closeDialog = () => {
    setCurrentMode(null);
    dragCountRef.current = 0;
  };

  const openDialog = (mode?: FileUploadMode) =>
    setCurrentMode(mode || FileUploadMode.Dropzone);

  return {
    getRootProps,
    getInputProps,
    closeDialog,
    currentMode,
    openDialog,
    onModeChange: setCurrentMode,
  };
};
